import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as warehouseActions from '../../actions/warehouse';
import { withRouter } from 'react-router';
import { toast } from 'react-toastify';
import moment from 'moment';

import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaTable, { MikaColumn } from '../../components/MikaTable/MikaTable';
import MikaDateFilter, { DateRange } from '../../components/MikaDateFilter/MikaDateFilter';
import MikaExport, { ExportColumn } from '../../components/MikaExport/MikaExport';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import ContactDashboardStrip from '../Contact/ContactDashboardStrip';
import '../../components/Pagination/Pagination.scss';
import '../Contact/Contact.scss';
import { axiosCaller } from '../../utils/apiCaller';
import MikaViewToggle from '../../components/MikaViewToggle/MikaViewToggle';
import WarehouseCardGrid from './WarehouseCardGrid';
import { useUrlFilters } from '../../hooks/useUrlFilters';
import './WarehouseMainPage.scss';

const isMobile = window.innerWidth < 576;
const VIEW_STORAGE_KEY = 'mikasoft_warehouse_view';

interface Props {
    intl: any;
    actions: any;
    warehouseActions: any;
    history: any;
    location: any;
    loading: boolean;
    permissions: any;
    warehouse: any;
}

type StripFilter = 'pendingReceipt' | 'inWarehouse' | 'shipped' | 'purchasedMaterials' | 'innerStock' | 'departments';

const STATUS_VISUAL: Record<string, { color: string; bg: string; id: string; defaultMessage: string }> = {
    pending_receipt:   { color: '#f59e0b', bg: '#fef3c7', id: 'warehouse.status.pending',     defaultMessage: 'Për pranim' },
    in_warehouse:      { color: '#3b82f6', bg: '#dbeafe', id: 'warehouse.status.inWarehouse', defaultMessage: 'Në depo' },
    shipped:           { color: '#22c55e', bg: '#dcfce7', id: 'warehouse.status.shipped',     defaultMessage: 'E dërguar' },
    partially_shipped: { color: '#8b5cf6', bg: '#f3e8ff', id: 'warehouse.status.partial',     defaultMessage: 'Pjesërisht' },
};

const getWaitTimeColor = (hours: number | null): { color: string; bg: string } => {
    if (hours === null) return { color: '#94a3b8', bg: '#f1f5f9' };
    if (hours < 24) return { color: '#22c55e', bg: '#dcfce7' };
    if (hours < 72) return { color: '#f59e0b', bg: '#fef3c7' };
    return { color: '#ef4444', bg: '#fef2f2' };
};

const WarehouseMainPage: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string, values?: Record<string, any>) =>
            intl.formatMessage({ id, defaultMessage }, values),
        [intl]
    );

    const formatWaitTime = useCallback((hours: number | null): string => {
        if (hours === null) return '\u2014';
        if (hours < 24) return fm('warehouse.time.hours', '{hours, plural, one {# orë} other {# orë}}', { hours });
        const days = Math.floor(hours / 24);
        return fm('warehouse.time.days', '{days, plural, one {# ditë} other {# ditë}}', { days });
    }, [fm]);

    const { filters, setFilter, setFilters, replaceFilters } = useUrlFilters(props.history, props.location, {
        page: { default: 1, type: 'number' },
        perPage: { default: 20, type: 'number' },
        search: { default: '', type: 'string' },
        strip: { default: 'pendingReceipt', type: 'string' },
        dateFrom: { default: '', type: 'string' },
        dateTo: { default: '', type: 'string' },
        dept: { default: '', type: 'string' },
    });

    const currentPage = filters.page as number;
    const perPage = filters.perPage as number;
    const searchTerm = filters.search as string;
    const stripFilter = (filters.strip || 'pendingReceipt') as StripFilter;
    const dateRange: DateRange | null = filters.dateFrom && filters.dateTo ? { from: filters.dateFrom, to: filters.dateTo } : null;
    const selectedDept = filters.dept || null;

    const setCurrentPage = (p: number) => setFilter('page', p);
    const setSearchTerm = (v: string) => replaceFilters({ search: v, page: 1 });
    const setStripFilter = (v: StripFilter) => setFilters({ strip: v, page: 1 });
    const setDateRange = (r: DateRange | null) => setFilters({ dateFrom: r?.from || '', dateTo: r?.to || '', page: 1 });
    const setSelectedDept = (v: string | null) => setFilter('dept', v || '');

    const [viewMode, setViewMode] = useState<'table' | 'card'>(() => {
        return (localStorage.getItem(VIEW_STORAGE_KEY) as 'table' | 'card') || 'table';
    });

    const handleViewChange = (mode: 'table' | 'card') => {
        setViewMode(mode);
        localStorage.setItem(VIEW_STORAGE_KEY, mode);
    };

    const effectiveView = isMobile ? 'table' : viewMode;
    const [statsData, setStatsData] = useState<any>(null);
    const [deptPopoverOpen, setDeptPopoverOpen] = useState(false);
    const [deptPopoverPos, setDeptPopoverPos] = useState({ top: 0, right: 0 });
    const deptRef = useRef<HTMLDivElement>(null);
    const stripRef = useRef<HTMLDivElement>(null);

    const [pendingData, setPendingData] = useState<any[]>([]);
    const [pendingPagination, setPendingPagination] = useState<any>({ currentPage: 1, lastPage: 1, perPage: 20, total: 0 });
    const [pendingLoading, setPendingLoading] = useState(false);
    const [receiveQuantities, setReceiveQuantities] = useState<Record<number, number>>({});
    const [processingIds, setProcessingIds] = useState<Set<number>>(new Set());

    const [materialsLoading, setMaterialsLoading] = useState(false);
    const [innerStockLoading, setInnerStockLoading] = useState(false);

    const [editingArticleId, setEditingArticleId] = useState<number | null>(null);
    const [editingQty, setEditingQty] = useState<number>(0);
    const [savingArticle, setSavingArticle] = useState(false);

    const [acceptModalOpen, setAcceptModalOpen] = useState(false);
    const [acceptProductId, setAcceptProductId] = useState<number | null>(null);
    const [acceptProductName, setAcceptProductName] = useState('');

    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectProductId, setRejectProductId] = useState<number | null>(null);
    const [rejectProductName, setRejectProductName] = useState('');
    const [rejectReason, setRejectReason] = useState('');
    const [rejectCategory, setRejectCategory] = useState('');

    useEffect(() => {
        props.warehouseActions.getWarehouseProducts(null, null, 1, 1, '');
        props.warehouseActions.getInnerStockProducts(1, 1, '');
    }, []);

    const getStatsContext = () => {
        if (stripFilter === 'pendingReceipt') return 'pending_receipt';
        if (stripFilter === 'shipped') return 'shipped';
        return 'in_warehouse';
    };

    useEffect(() => {
        axiosCaller('GET', `warehouse/dashboard-stats?context=${getStatsContext()}`).then((res: any) => {
            const data = res?.data || res;
            if (data) setStatsData(data);
        }).catch(() => {});
    }, [stripFilter]);

    const fetchPendingData = () => {
        setPendingLoading(true);
        let qp: string[] = [`page=${currentPage}`, `per_page=${perPage}`];
        if (searchTerm) qp.push(`search=${encodeURIComponent(searchTerm)}`);
        if (dateRange?.from && dateRange?.to) { qp.push(`start=${dateRange.from}`); qp.push(`end=${dateRange.to}`); }
        axiosCaller('GET', `warehouse/receiving?${qp.join('&')}`)
            .then((res: any) => {
                const responseData = res?.data;
                setPendingData(responseData?.data || []);
                setPendingPagination({
                    currentPage: responseData?.meta?.current_page || 1,
                    lastPage: responseData?.meta?.last_page || 1,
                    perPage: responseData?.meta?.per_page || 20,
                    total: responseData?.meta?.total || 0,
                });
                const qtyMap: Record<number, number> = {};
                (responseData?.data || []).forEach((p: any) => {
                    qtyMap[p.id] = p.pendingQuantity ?? p.quantity ?? 1;
                });
                setReceiveQuantities(qtyMap);
            })
            .catch(() => {})
            .finally(() => setPendingLoading(false));
    };

    const fetchInventoryData = () => {
        const status = stripFilter === 'inWarehouse' ? 'in_warehouse' : 'shipped';
        props.warehouseActions.getWarehouseInventory(
            currentPage, perPage, searchTerm, status,
            dateRange?.from || '', dateRange?.to || '',
            selectedDept || ''
        );
    };

    const fetchMaterialsData = () => {
        setMaterialsLoading(true);
        props.warehouseActions.getWarehouseProducts(null, null, currentPage, perPage, searchTerm);
        setMaterialsLoading(false);
    };

    const fetchInnerStockData = () => {
        setInnerStockLoading(true);
        props.warehouseActions.getInnerStockProducts(currentPage, perPage, searchTerm);
        setInnerStockLoading(false);
    };

    useEffect(() => {
        if (stripFilter === 'pendingReceipt') {
            fetchPendingData();
        } else if (stripFilter === 'purchasedMaterials') {
            fetchMaterialsData();
        } else if (stripFilter === 'innerStock') {
            fetchInnerStockData();
        } else if (stripFilter !== 'departments') {
            fetchInventoryData();
        }
    }, [props.location.search]);

    const inventoryData = props.warehouse.warehouseInventory || [];
    const inventoryPagination = props.warehouse.warehouseInventoryPagination;
    const materialsData = props.warehouse.warehouseProducts || [];
    const materialsPagination = props.warehouse.warehouseProductsPagination;
    const innerStockData = props.warehouse.warehouseInnerStock || [];
    const innerStockPagination = props.warehouse.warehouseInnerStockPagination;

    const isPending = stripFilter === 'pendingReceipt';
    const isInWarehouse = stripFilter === 'inWarehouse';
    const isMaterials = stripFilter === 'purchasedMaterials';
    const isInnerStock = stripFilter === 'innerStock';

    const dashboardStats = useMemo(() => {
        return [
            { key: 'pendingReceipt',     label: fm('warehouse.status.pending',              'Për pranim'),        value: statsData?.pendingReceipt ?? '\u2014',    icon: 'alert' },
            { key: 'inWarehouse',        label: fm('warehouse.status.inWarehouse',          'Në depo'),           value: statsData?.inWarehouse ?? '\u2014',       icon: 'folder' },
            { key: 'shipped',            label: fm('warehouse.strip.shipped',               'Të dërguara'),       value: statsData?.shipped ?? '\u2014',           icon: 'check' },
            { key: 'purchasedMaterials', label: fm('warehouse.strip.purchasedMaterials',    'Artikujt e blerë'),  value: materialsPagination?.total ?? '\u2014',   icon: 'box' },
            { key: 'innerStock',         label: fm('warehouse.strip.innerStock',            'Stoku i brendshëm'), value: innerStockPagination?.total ?? '\u2014',  icon: 'layers' },
            { key: 'departments',        label: fm('warehouse.strip.departments',           'Repartet'),          value: (statsData?.departmentDistribution || []).length || '\u2014', icon: 'map' },
        ];
    }, [statsData, materialsPagination, innerStockPagination, fm]);

    const deptDistribution = useMemo(() => {
        const dist = statsData?.departmentDistribution;
        if (!dist || !Array.isArray(dist)) return [];
        return dist.sort((a: any, b: any) => b.count - a.count);
    }, [statsData]);

    const positionPopoverUnderCard = (cardIndex: number) => {
        if (!stripRef.current) return { top: 0, right: 0 };
        const cards = stripRef.current.querySelectorAll('.cds-card');
        const card = cards[cardIndex];
        if (!card) return { top: 0, right: 0 };
        const rect = card.getBoundingClientRect();
        const popoverWidth = 220;
        const cardCenter = rect.left + rect.width / 2;
        return { top: rect.bottom + 4, right: window.innerWidth - cardCenter - popoverWidth / 2 };
    };

    const handleStripClick = (key: string) => {
        if (key === 'departments') {
            if (!deptPopoverOpen) setDeptPopoverPos(positionPopoverUnderCard(5));
            setDeptPopoverOpen(!deptPopoverOpen);
            return;
        }
        setDeptPopoverOpen(false);
        const newFilter = key as StripFilter;
        if (newFilter === stripFilter) return;
        setStripFilter(newFilter);
    };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (deptRef.current && !deptRef.current.contains(e.target as Node)) setDeptPopoverOpen(false);
        };
        if (deptPopoverOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [deptPopoverOpen]);

    const openAcceptModal = (productId: number, productName: string) => {
        setAcceptProductId(productId);
        setAcceptProductName(productName);
        setAcceptModalOpen(true);
    };

    const handleAccept = async () => {
        if (!acceptProductId) return;
        const qty = receiveQuantities[acceptProductId];
        if (!qty || qty <= 0) return;
        setAcceptModalOpen(false);
        setProcessingIds(prev => new Set(prev).add(acceptProductId));
        try {
            const res = await props.warehouseActions.receiveProduct(acceptProductId, qty);
            if (res?.success) {
                toast.success(fm('warehouse.toast.productAccepted', 'Produkti u pranua me sukses'));
                fetchPendingData();
                axiosCaller('GET', `warehouse/dashboard-stats?context=${getStatsContext()}`).then((r: any) => { if (r?.data) setStatsData(r.data); }).catch(() => {});
            }
        } finally {
            setProcessingIds(prev => { const n = new Set(prev); n.delete(acceptProductId!); return n; });
        }
    };

    const openRejectModal = (productId: number, productName: string) => {
        setRejectProductId(productId);
        setRejectProductName(productName);
        setRejectReason('');
        setRejectCategory('');
        setRejectModalOpen(true);
    };

    const handleReject = async () => {
        if (!rejectProductId || !rejectReason.trim() || !rejectCategory.trim()) {
            toast.error(fm('warehouse.toast.fillRejectFields', 'Plotësoni arsyen dhe kategorinë'));
            return;
        }
        setProcessingIds(prev => new Set(prev).add(rejectProductId));
        setRejectModalOpen(false);
        try {
            const res = await props.warehouseActions.rejectProduct(rejectProductId, rejectCategory, rejectReason);
            if (res?.success) {
                toast.info(fm('warehouse.toast.productRejected', 'Produkti u kthye në prodhim'));
                fetchPendingData();
                axiosCaller('GET', `warehouse/dashboard-stats?context=${getStatsContext()}`).then((r: any) => { if (r?.data) setStatsData(r.data); }).catch(() => {});
            }
        } finally {
            setProcessingIds(prev => { const n = new Set(prev); n.delete(rejectProductId!); return n; });
        }
    };

    const pendingColumns: MikaColumn[] = useMemo(() => [
        { key: 'articleName', title: fm('production.board.productCol', 'Produkti'), render: (val: any, row: any) => (
            <div>
                <span style={{ fontWeight: 500, color: '#1e293b' }}>{val || '\u2014'}</span>
                {row.typeCode && <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 6 }}>({row.typeCode})</span>}
            </div>
        )},
        { key: 'clientName', title: fm('production.detail.client', 'Klienti'), render: (val: any, row: any) => (
            <span style={{ color: '#475569' }}>{[val, row.clientLastName].filter(Boolean).join(' ')}</span>
        )},
        { key: 'projectName', title: fm('production.detail.project', 'Projekti'), hideOnMobile: true },
        { key: 'departmentName', title: fm('production.responsibilityArchive.card.department', 'Reparti'), hideOnMobile: true, render: (val: any) => val || '\u2014' },
        {
            key: 'pendingQuantity', title: fm('production.detail.quantity', 'Sasia'), width: '130px',
            render: (_: any, row: any) => {
                const total = row.quantity ?? 1;
                const pending = row.pendingQuantity ?? total;
                const isProcessing = processingIds.has(row.id);
                return (
                    <div className="wmp-qty-cell" onClick={(e) => e.stopPropagation()}>
                        <input
                            type="number" step="any" min={1} max={pending}
                            value={receiveQuantities[row.id] ?? pending}
                            onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                setReceiveQuantities(prev => ({ ...prev, [row.id]: Math.min(Math.max(val, 0), pending) }));
                            }}
                            className="wmp-qty-input"
                            disabled={isProcessing}
                        />
                        <span className="wmp-qty-total">/ {total}</span>
                    </div>
                );
            }
        },
        {
            key: 'pendingSinceHours', title: fm('warehouse.col.waitTimePending', 'Koha në pritje'),
            hideOnMobile: true,
            render: (val: any) => {
                const wt = getWaitTimeColor(val);
                return <span style={{ color: wt.color, background: wt.bg, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500 }}>{formatWaitTime(val)}</span>;
            }
        },
        {
            key: '_actions', title: fm('common.actions', 'Veprimet'), width: '100px',
            render: (_: any, row: any) => {
                const isProcessing = processingIds.has(row.id);
                return (
                    <div className="wmp-action-cell" onClick={(e) => e.stopPropagation()}>
                        <button className="wmp-accept-btn" onClick={() => openAcceptModal(row.id, row.articleName)} disabled={isProcessing}
                            title={fm('warehouse.action.accept', 'Prano')}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                        </button>
                        <button className="wmp-reject-btn" onClick={() => openRejectModal(row.id, row.articleName)} disabled={isProcessing}
                            title={fm('warehouse.action.reject', 'Kthe')}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                    </div>
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale, receiveQuantities, processingIds]);

    const inventoryColumns: MikaColumn[] = useMemo(() => [
        { key: 'id', title: '#', sortable: true, width: '50px', render: (val: any) => <span style={{ color: '#94a3b8', fontSize: '11px' }}>{val}</span> },
        { key: 'articleName', title: fm('production.board.productCol', 'Produkti'), sortable: true },
        { key: 'clientName', title: fm('production.detail.client', 'Klienti'), sortable: true },
        { key: 'projectName', title: fm('production.detail.project', 'Projekti'), hideOnMobile: true },
        {
            key: 'quantity', title: fm('production.detail.quantity', 'Sasia'),
            render: (val: any, row: any) => {
                if (isInWarehouse && row.receivedQuantity && row.receivedQuantity < val) {
                    return `${row.receivedQuantity} / ${val}`;
                }
                return val ?? '\u2014';
            }
        },
        {
            key: 'warehouseStatus', title: fm('production.products.column.status', 'Statusi'),
            render: (val: any) => {
                const s = STATUS_VISUAL[val] || STATUS_VISUAL['in_warehouse'];
                return <span style={{ color: s.color, background: s.bg, padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500, display: 'inline-block' }}>{fm(s.id, s.defaultMessage)}</span>;
            }
        },
        {
            key: 'waitTimeHours', title: fm('warehouse.col.waitTimeStorage', 'Koha në depo'),
            sortable: true,
            render: (val: any) => {
                const wt = getWaitTimeColor(val);
                return <span style={{ color: wt.color, background: wt.bg, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500 }}>{formatWaitTime(val)}</span>;
            }
        },
        ...(isInWarehouse ? [
            {
                key: 'receivedBy', title: fm('warehouse.col.receivedBy', 'Pranuar nga'),
                hideOnMobile: true, render: (val: any) => val || '\u2014'
            } as MikaColumn,
            {
                key: 'receivedAt', title: fm('warehouse.col.receivedAt', 'Pranuar më'),
                sortable: true, hideOnMobile: true, render: (val: any) => val || '\u2014'
            } as MikaColumn,
        ] : [{
            key: 'shippedAt', title: fm('warehouse.col.shippedAt', 'Dërguar më'),
            sortable: true, hideOnMobile: true, render: (val: any) => val || '\u2014'
        } as MikaColumn]),
        ...(props.permissions?.fullPrivileges ? [{
            key: 'price', title: fm('production.detail.value', 'Vlera'),
            hideOnMobile: true, render: (val: any) => val ? `${Number(val).toFixed(2)}\u20AC` : '\u2014'
        } as MikaColumn] : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale, isInWarehouse, props.permissions]);

    const handleStartEdit = (row: any) => {
        setEditingArticleId(row.value);
        setEditingQty(row.quantity ?? 0);
    };

    const handleCancelEdit = () => {
        setEditingArticleId(null);
        setEditingQty(0);
    };

    const handleSaveEdit = async () => {
        if (editingArticleId === null) return;
        setSavingArticle(true);
        try {
            await axiosCaller('POST', 'warehouse/update', { warehouseId: editingArticleId, quantity: editingQty });
            toast.success(fm('warehouse.toast.quantityUpdated', 'Sasia u përditësua'));
            setEditingArticleId(null);
            fetchMaterialsData();
        } catch {
            toast.error(fm('production.toast.somethingWentWrong', 'Diçka nuk shkoi mirë!'));
        } finally {
            setSavingArticle(false);
        }
    };

    const materialsColumns: MikaColumn[] = useMemo(() => [
        { key: 'articleName', title: fm('warehouse.col.article', 'Artikulli'), sortable: true, render: (val: any) => val || '\u2014' },
        { key: 'productCode', title: fm('warehouse.col.productCode', 'Kodi'), hideOnMobile: true, render: (val: any) => val ? <span style={{ color: '#94a3b8', fontSize: 11 }}>{val}</span> : '\u2014' },
        {
            key: 'quantity', title: fm('production.detail.quantity', 'Sasia'), sortable: true, width: '140px',
            render: (val: any, row: any) => {
                const isEditing = editingArticleId === row.value;
                if (isEditing) {
                    return (
                        <div className="wmp-inline-edit" onClick={(e) => e.stopPropagation()}>
                            <input type="number" step="any" min={0} value={editingQty}
                                onChange={(e) => setEditingQty(parseFloat(e.target.value) || 0)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') handleCancelEdit(); }}
                                className="wmp-inline-input" autoFocus disabled={savingArticle} />
                            <button className="wmp-inline-confirm" onClick={handleSaveEdit} disabled={savingArticle}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                            </button>
                            <button className="wmp-inline-cancel" onClick={handleCancelEdit} disabled={savingArticle}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                    );
                }
                return <span style={{ fontWeight: 600 }}>{val ?? '\u2014'}</span>;
            }
        },
        { key: 'unitName', title: fm('warehouse.col.unit', 'Njësia'), hideOnMobile: true, render: (val: any) => val || '\u2014' },
        { key: 'departmentName', title: fm('production.responsibilityArchive.card.department', 'Reparti'), hideOnMobile: true, render: (val: any) => val || '\u2014' },
        { key: 'pricePerUnit', title: fm('warehouse.col.pricePerUnit', 'Çmimi/njësi'), hideOnMobile: true, render: (val: any) => val ? `${Number(val).toFixed(2)}\u20AC` : '\u2014' },
        { key: 'totalValue', title: fm('warehouse.col.totalValue', 'Vlera totale'), hideOnMobile: true, render: (val: any) => val ? `${Number(val).toFixed(2)}\u20AC` : '\u2014' },
        { key: 'updatedAt', title: fm('warehouse.col.lastUpdated', 'Përditësuar'), hideOnMobile: true, render: (val: any) => val || '\u2014' },
        {
            key: '_edit', title: '', width: '50px',
            render: (_: any, row: any) => {
                if (editingArticleId === row.value) return null;
                return (
                    <button className="wmp-edit-btn" onClick={(e) => { e.stopPropagation(); handleStartEdit(row); }}
                        title={fm('warehouse.action.editQuantity', 'Ndrysho sasinë')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                    </button>
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale, editingArticleId, editingQty, savingArticle]);

    const innerStockColumns: MikaColumn[] = useMemo(() => [
        { key: 'designation.value', title: '#', sortable: true, width: '50px', render: (val: any) => <span style={{ color: '#94a3b8', fontSize: '11px' }}>{val}</span> },
        { key: 'designation.label', title: fm('production.board.productCol', 'Produkti'), sortable: true, render: (val: any) => (val && typeof val !== 'object' ? val : '\u2014') },
        { key: 'clientName', title: fm('production.detail.client', 'Klienti'), sortable: true, render: (val: any) => (val && typeof val !== 'object' ? val : '\u2014') },
        { key: 'project.name', title: fm('production.detail.project', 'Projekti'), hideOnMobile: true, render: (val: any) => (val && typeof val !== 'object' ? val : '\u2014') },
        { key: 'department.label', title: fm('production.responsibilityArchive.card.department', 'Reparti'), hideOnMobile: true, render: (val: any) => (val && typeof val !== 'object' ? val : '\u2014') },
        { key: 'unit', title: fm('production.detail.quantity', 'Sasia'), render: (val: any) => (val && typeof val !== 'object' ? val : '\u2014') },
        { key: 'finishedProduct', title: fm('warehouse.col.completedOn', 'Përfunduar më'), sortable: true, render: (val: any) => val ? moment(typeof val === 'object' && val.updatedAt ? val.updatedAt : val).format('DD/MM/YYYY HH:mm') : '\u2014' },
        ...(props.permissions?.fullPrivileges ? [{ key: 'price', title: fm('production.detail.value', 'Vlera'), hideOnMobile: true, render: (val: any) => val ? `${Number(val).toFixed(2)}\u20AC` : '\u2014' } as MikaColumn] : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale, props.permissions]);

    const inventoryActions = isInWarehouse ? [
        {
            icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>,
            tooltip: fm('warehouse.action.createShipment', 'Krijo dërgesë'),
            onClick: (row: any) => props.history.push(`/dashboard/warehouse/create/${row.clientId}/${row.projectId}`)
        }
    ] : [];

    const innerStockActions = [
        {
            icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
            tooltip: fm('common.action.edit', 'Ndrysho'),
            onClick: (row: any) => props.history.push(`/dashboard/warehouse/create/${row.clientID}`)
        }
    ];

    const exportColumns: ExportColumn[] = useMemo(() => {
        if (isInnerStock) {
            return [
                { key: 'designation.value', title: '#' },
                { key: 'designation.label', title: fm('production.board.productCol', 'Produkti'), render: (r) => r?.designation?.label && typeof r.designation.label !== 'object' ? r.designation.label : '' },
                { key: 'clientName',        title: fm('production.detail.client', 'Klienti') },
                { key: 'project.name',      title: fm('production.detail.project', 'Projekti'), render: (r) => r?.project?.name && typeof r.project.name !== 'object' ? r.project.name : '' },
                { key: 'department.label',  title: fm('production.responsibilityArchive.card.department', 'Reparti'), render: (r) => r?.department?.label && typeof r.department.label !== 'object' ? r.department.label : '' },
                { key: 'unit',              title: fm('production.detail.quantity', 'Sasia'), render: (r) => r?.unit !== null && r?.unit !== undefined && typeof r.unit !== 'object' ? String(r.unit) : '' },
                { key: 'finishedProduct',   title: fm('warehouse.col.completedOn', 'Përfunduar më'), render: (r) => r?.finishedProduct && typeof r.finishedProduct !== 'object' ? r.finishedProduct : (r?.finishedProduct?.updatedAt || '') },
                { key: 'price',             title: fm('production.detail.value', 'Vlera'), render: (r) => r?.price ? `${Number(r.price).toFixed(2)}\u20AC` : '' },
            ];
        }
        if (isMaterials) {
            return [
                { key: 'articleName',    title: fm('warehouse.col.article', 'Artikulli') },
                { key: 'productCode',    title: fm('warehouse.col.productCode', 'Kodi') },
                { key: 'quantity',       title: fm('production.detail.quantity', 'Sasia') },
                { key: 'unitName',       title: fm('warehouse.col.unit', 'Njësia') },
                { key: 'departmentName', title: fm('production.responsibilityArchive.card.department', 'Reparti') },
                { key: 'pricePerUnit',   title: fm('warehouse.col.pricePerUnit', 'Çmimi/njësi'), render: (r) => r?.pricePerUnit ? `${Number(r.pricePerUnit).toFixed(2)}\u20AC` : '' },
                { key: 'totalValue',     title: fm('warehouse.col.totalValue', 'Vlera totale'), render: (r) => r?.totalValue ? `${Number(r.totalValue).toFixed(2)}\u20AC` : '' },
                { key: 'updatedAt',      title: fm('warehouse.col.lastUpdated', 'Përditësuar'), render: (r) => r?.updatedAt && typeof r.updatedAt !== 'object' ? r.updatedAt : '' },
            ];
        }
        if (isPending) {
            return [
                { key: 'articleName',        title: fm('production.board.productCol', 'Produkti') },
                { key: 'clientName',         title: fm('production.detail.client', 'Klienti') },
                { key: 'projectName',        title: fm('production.detail.project', 'Projekti') },
                { key: 'departmentName',     title: fm('production.responsibilityArchive.card.department', 'Reparti') },
                { key: 'pendingQuantity',    title: fm('production.detail.quantity', 'Sasia') },
                { key: 'warehousePendingAt', title: fm('warehouse.col.waitTimePending', 'Koha në pritje') },
            ];
        }
        // inWarehouse / shipped
        return [
            { key: 'id',              title: '#' },
            { key: 'articleName',     title: fm('production.board.productCol', 'Produkti') },
            { key: 'clientName',      title: fm('production.detail.client', 'Klienti') },
            { key: 'projectName',     title: fm('production.detail.project', 'Projekti') },
            { key: 'quantity',        title: fm('production.detail.quantity', 'Sasia') },
            { key: 'warehouseStatus', title: fm('production.products.column.status', 'Statusi'), render: (r) => {
                const s = STATUS_VISUAL[r?.warehouseStatus];
                return s ? fm(s.id, s.defaultMessage) : (r?.warehouseStatus || '');
            } },
            { key: 'waitTimeDays',    title: fm('warehouse.col.waitDays', 'Ditë në depo') },
            { key: isInWarehouse ? 'receivedAt' : 'shippedAt', title: fm(isInWarehouse ? 'warehouse.col.receivedAt' : 'warehouse.col.shippedAt', isInWarehouse ? 'Pranuar më' : 'Dërguar më') },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intl.locale, isPending, isInWarehouse, isMaterials, isInnerStock]);

    const fetchAllForExport = async (): Promise<any[]> => {
        if (isPending) {
            const res = await axiosCaller('GET', 'warehouse/receiving?per_page=0');
            return (res as any)?.data?.data || [];
        }
        if (isMaterials) {
            const res = await axiosCaller('GET', 'warehouse/products?per_page=0');
            return (res as any)?.data?.data || (res as any)?.data || materialsData;
        }
        if (isInnerStock) {
            const res = await axiosCaller('GET', 'warehouse-records/inner/stock?per_page=0');
            return (res as any)?.data?.data || (res as any)?.data || innerStockData;
        }
        const status = isInWarehouse ? 'in_warehouse' : 'shipped';
        let qp: string[] = ['per_page=0'];
        if (searchTerm) qp.push(`search=${encodeURIComponent(searchTerm)}`);
        if (status) qp.push(`status=${status}`);
        if (dateRange?.from && dateRange?.to) { qp.push(`start=${dateRange.from}`); qp.push(`end=${dateRange.to}`); }
        const res = await axiosCaller('GET', `warehouse/inventory?${qp.join('&')}`);
        return (res as any)?.data?.data || (res as any)?.data || [];
    };

    const getActiveConfig = () => {
        if (isPending) return { data: pendingData, pagination: pendingPagination, columns: pendingColumns, actions: [] as any[], loading: pendingLoading };
        if (isMaterials) return { data: materialsData, pagination: materialsPagination, columns: materialsColumns, actions: [] as any[], loading: materialsLoading };
        if (isInnerStock) return { data: innerStockData, pagination: innerStockPagination, columns: innerStockColumns, actions: innerStockActions, loading: innerStockLoading };
        return { data: inventoryData, pagination: inventoryPagination, columns: inventoryColumns, actions: inventoryActions, loading: props.loading };
    };

    const { data: activeData, pagination: activePagination, columns: activeColumns, actions: activeActions, loading: activeLoading } = getActiveConfig();

    return (
        <div className="clp-page">
            {!isMobile && (
                <MikaPageHeader
                    title={fm('warehouse.title', 'Depo')}
                    subtitle={activePagination ? `${activePagination.total} total` : undefined}
                />
            )}

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input type="text" className="clp-search-input"
                        placeholder={fm('warehouse.searchPlaceholder', 'Kërko në depo...')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="clp-search-clear" onClick={() => setSearchTerm('')} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    <MikaDateFilter value={dateRange} onChange={(r) => setDateRange(r)} />
                    <MikaExport fetchAllData={fetchAllForExport} columns={exportColumns} fileName={fm('warehouse.exportFilename', 'depo')} title={fm('warehouse.title', 'Depo')} companyProfile={(props as any).companyProfile} />
                    {!isMobile && <MikaViewToggle mode={viewMode} onChange={handleViewChange} />}
                </div>
            </div>

            {(
                <>
                    <div ref={stripRef}>
                        <ContactDashboardStrip stats={dashboardStats} activeFilter={stripFilter} onFilterClick={handleStripClick} />
                    </div>
                    {deptPopoverOpen && (
                        <div className="clp-city-popover" ref={deptRef} style={{ top: deptPopoverPos.top, right: deptPopoverPos.right }}>
                            <div className="clp-city-header"><FormattedMessage id="warehouse.departmentsHeader" defaultMessage="Repartet" /></div>
                            {selectedDept && (
                                <div className="clp-city-row clp-city-row-clear" style={{ cursor: 'pointer' }} onClick={() => setSelectedDept(null)}>
                                    <span className="clp-city-name" style={{ color: '#ef4444' }}><FormattedMessage id="warehouse.clearFilter" defaultMessage="Pastro filtrin" /></span>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                </div>
                            )}
                            {deptDistribution.length === 0 ? <div className="clp-city-empty"><FormattedMessage id="common.noData" defaultMessage="Nuk ka të dhëna" /></div> :
                                deptDistribution.map((dept: any) => (
                                    <div key={dept.id} className={`clp-city-row ${selectedDept === String(dept.id) ? 'clp-city-row-active' : ''}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setSelectedDept(selectedDept === String(dept.id) ? null : String(dept.id))}>
                                        <span className="clp-city-name">{dept.name}</span><span className="clp-city-count">{dept.count}</span>
                                    </div>
                                ))
                            }
                        </div>
                    )}
                </>
            )}

            {effectiveView === 'table' ? (
                <MikaTable
                    columns={activeColumns}
                    data={activeData}
                    actions={activeActions}
                    loading={activeLoading}
                    onRowClick={isInWarehouse ? (row: any) => props.history.push(`/dashboard/warehouse/create/${row.clientId}/${row.projectId}`) : undefined}
                />
            ) : (
                <WarehouseCardGrid
                    data={activeData}
                    tab={stripFilter as any}
                    onRowClick={isInWarehouse ? (row: any) => props.history.push(`/dashboard/warehouse/create/${row.clientId}/${row.projectId}`) : undefined}
                    onAccept={isPending ? (row: any) => openAcceptModal(row.id, row.articleName) : undefined}
                    onReject={isPending ? (row: any) => openRejectModal(row.id, row.articleName) : undefined}
                    onEditArticle={isMaterials ? (row: any) => handleStartEdit(row) : undefined}
                />
            )}

            <PaginationComponent
                currentPage={currentPage} perPage={perPage} pagination={activePagination}
                onPageChange={(p) => setFilter('page', p)}
                onPerPageChange={(v) => setFilters({ perPage: v, page: 1 })}
                label={fm('warehouse.pagination.label', 'produkte')}
            />

            {/* Accept confirmation popup */}
            {acceptModalOpen && (
                <div className="wmp-overlay" onClick={() => setAcceptModalOpen(false)}>
                    <div className="wmp-popup" onClick={(e) => e.stopPropagation()}>
                        <p className="wmp-popup-title"><FormattedMessage id="warehouse.acceptModal.title" defaultMessage="Pranoni produktin?" /></p>
                        <p className="wmp-popup-subtitle">{acceptProductName}</p>
                        <p className="wmp-popup-desc">
                            <FormattedMessage id="warehouse.acceptModal.description" defaultMessage="Konfirmoni që produkti është në gjendje të mirë dhe gati për depo." />
                        </p>
                        {acceptProductId && receiveQuantities[acceptProductId] !== undefined && (
                            <p className="wmp-popup-qty">
                                <FormattedMessage id="warehouse.acceptModal.quantity" defaultMessage="Sasia" />: <strong>{receiveQuantities[acceptProductId]}</strong>
                            </p>
                        )}
                        <div className="wmp-popup-actions">
                            <button className="wmp-popup-cancel" onClick={() => setAcceptModalOpen(false)}>
                                <FormattedMessage id="common.action.cancel" defaultMessage="Anulo" />
                            </button>
                            <button className="wmp-popup-confirm-accept" onClick={handleAccept}>
                                <FormattedMessage id="warehouse.acceptModal.confirm" defaultMessage="Po, pranoje" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject popup */}
            {rejectModalOpen && (
                <div className="wmp-overlay" onClick={() => setRejectModalOpen(false)}>
                    <div className="wmp-popup" onClick={(e) => e.stopPropagation()}>
                        <p className="wmp-popup-title"><FormattedMessage id="warehouse.rejectModal.title" defaultMessage="Kthe në prodhim?" /></p>
                        <p className="wmp-popup-subtitle">{rejectProductName}</p>
                        <select value={rejectCategory} onChange={(e) => setRejectCategory(e.target.value)} className="wmp-popup-select">
                            <option value="">{fm('warehouse.rejectModal.selectCategory', 'Kategoria...')}</option>
                            <option value="Gabim prodhimi">{fm('warehouse.rejectModal.productionError', 'Gabim prodhimi')}</option>
                            <option value="D\u00ebmtim gjat\u00eb transportit">{fm('warehouse.rejectModal.transportDamage', 'Dëmtim gjatë transportit')}</option>
                            <option value="Cil\u00ebsi e ul\u00ebt">{fm('warehouse.rejectModal.lowQuality', 'Cilësi e ulët')}</option>
                            <option value="Specifik\u00eb e gabuar">{fm('warehouse.rejectModal.wrongSpec', 'Specifikë e gabuar')}</option>
                            <option value="Tjet\u00ebr">{fm('warehouse.rejectModal.other', 'Tjetër')}</option>
                        </select>
                        <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={2}
                            placeholder={fm('warehouse.rejectModal.reasonPlaceholder', 'Arsyeja...')} className="wmp-popup-textarea" />
                        <div className="wmp-popup-actions">
                            <button className="wmp-popup-cancel" onClick={() => setRejectModalOpen(false)}>
                                <FormattedMessage id="common.action.cancel" defaultMessage="Anulo" />
                            </button>
                            <button className="wmp-popup-confirm-reject" onClick={handleReject} disabled={!rejectReason.trim() || !rejectCategory}>
                                <FormattedMessage id="warehouse.rejectModal.confirm" defaultMessage="Kthe në prodhim" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        warehouse: state.warehouse,
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        warehouseActions: bindActionCreators(warehouseActions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(WarehouseMainPage as any)));
